import { CAPI, CAPIFeature } from '@grammarly/ws-clients-capi'
import { Change, WS } from '@grammarly/ws-clients'
import { Cheetah } from '@grammarly/http-clients-cheetah'
import { Meta as CheetahMeta } from '@grammarly/cheetah-client-protocol'

import { getConfig } from '../../../config'
import { BackendPromptCap } from '@components/section/go-templates-tool/local-storage-state'
import { logError, LogErrorMetrics } from '@lib/tracking/felog'
import { GGOMetadata } from '@lib/services/api/types'

import {
  CheetahChunkMessage,
  CheetahErrorMessage,
  CheetahMessageAction,
  CheetahResultMessage,
  CheetahSetContextMessage,
  WSError,
} from './cheetah-utils'

import { clientNameFunnel } from './fingerprints'
import { getLogger } from '@lib/tracking/felog/telemetry'

// On form submit, the results state is loading and when the chunks start streaming in, the state is changed to streaming
// When the result is complete, the state should change to done
// The cheetah callback handlers are used to set the appropriate state
export type CheetahResultState = 'initial' | 'loading' | 'streaming' | 'done' | 'error'

// Some users manage to send an action without getting a set context message.
// We believe this is due to them going through the flow faster than the message comes back (rather than some sort of error).
// Instead of disabling the flow until the set context message returns, we mock out the context id and state blob since they are
// simple for our scenario and we want the user to be able to continue.
// This does seem to cause GGO not to stream, and maybe other effects.
const contextIdMock = '1'

// #ToDo update the mocked state blob to be stateless
const stateBlobMock =
  '{"preset":{"assistant_flags":{"assistant_mode":"initial_actions","start_session":true}},"_capi":{"ss":{"action":"cheetah:startSession","id":3,"rev":0,"entryPoint":"ideation","selectedRange":{"start":0,"end":0}},"c":{"voice":{"selectedStyle":"Neutral","selectedTones":[],"needSetup":true},"availableActions":[{"id":"action:?id\\u003dUseCaseToolPrompt\\u0026pname\\u003dCheetah/Compose/UseCaseTool/Generic","args":[{"valueType":"string","name":"answer"}],"generatesTextType":"insertable"}],"parentAction":{"contextId":"0?uid\\u003da916d443-b35c-449b-93e2-e8067d400869","actionId":"action:?id\\u003dstartSession\\u0026pname\\u003d","args":[]},"promptAction":{"id":"action:?id\\u003duserPrompt\\u0026pname\\u003dCheetah/_System/Compose/UserPrompt","args":[{"valueType":"string","name":"userPrompt"}],"generatesTextType":"insertable"},"quickRewriteAction":{"id":"action:?id\\u003dImFeelingLucky\\u0026pname\\u003dCheetah/_System/Rewrite/Magic","args":[],"generatesTextType":"insertable"}}}}'
const actionId = 'action:?id=UseCaseToolPrompt&pname=Cheetah/Compose/UseCaseTool/Generic'

interface CapiWsClientCheetahCallbacks {
  onCheetahResult: (e: CheetahResultMessage) => void
  onCheetahChunk: (e: CheetahChunkMessage) => void
  onCheetahError: (e: CheetahErrorMessage) => void
  onCheetahSetContext: (e: CheetahSetContextMessage) => void
  onError: (e: unknown) => void
  onCheetahActionSend?: () => void
}

interface CapiWsClientCallbacks {
  onSyncBackendPromptCap: (cap: BackendPromptCap) => void
  onTriggerWall: () => void
}

interface CapiWsClientOptions {
  cheetah: Cheetah
  cheetahCallbacks: CapiWsClientCheetahCallbacks
  callbacks: CapiWsClientCallbacks
  isAnonymous: boolean
  gnarContainerId: Promise<string>
}

export class CapiWsClient {
  private capiWsConnection: CAPI.CAPIConnection | undefined = undefined
  private meta: GGOMetadata = { highestChunkId: 0, contextId: contextIdMock }
  private isCheetahSessionStarted: boolean = false
  private stateBlob: string | undefined = stateBlobMock
  private messageQueue: string | undefined = undefined
  private messageQueueTimeout: ReturnType<typeof setTimeout> | null = null
  private options: CapiWsClientOptions
  private gnarContainerIdCache: string | undefined = undefined

  constructor(options: CapiWsClientOptions) {
    this.options = options
  }

  // =====================================
  // PUBLIC METHODS
  // =====================================
  async initiateWSConnection(): Promise<void> {
    try {
      await this.assertCheetahEnabled()
      await this.getGnarContainerId()
      const capiClientOptions: CAPI.CAPIClientOptions = {
        client: { type: clientNameFunnel, sub: 'general' },
        clientVersion: '1.0.0-cms',
        dialect: 'american',
        clientSupports: [CAPIFeature.cheetah, CAPIFeature.reconnect],
        clientConfigs: {
          cheetah: {
            version: CheetahMeta.version,
          },
        },
        isDemoDoc: false,
        handler: {
          onSessionStarted: this.onCapiSessionStarted,
          onError: this.onCapiErrorReceived,
          onDisconnect: this.onCapiDisconnect,
        },
      }

      const capi = new CAPI.CAPIConnection(
        {
          ...capiClientOptions,
        },
        (w) => w.connect(getConfig().urls.capiWs),
        new CAPI.AccessTokenWSHandler(WS.create(), '')
      )

      capi.client.registerExternalMessageHandler((msg) => {
        switch (msg.action) {
          // @ts-expect-error Due to lack of proper typings for cheetah actions
          case CheetahMessageAction.Ack:
            return CAPI.ExternalHandler.Result.STOP_HANDLING
          // @ts-expect-error Due to lack of proper typings for cheetah actions
          case CheetahMessageAction.Result:
            this.onCheetahResult(msg)
            return CAPI.ExternalHandler.Result.STOP_HANDLING
          // @ts-expect-error Due to lack of proper typings for cheetah actions
          case CheetahMessageAction.Chunk:
            this.onCheetahChunk(msg)
            return CAPI.ExternalHandler.Result.STOP_HANDLING
          // @ts-expect-error Due to lack of proper typings for cheetah actions
          case CheetahMessageAction.Error:
            this.onCheetahError(msg)
            return CAPI.ExternalHandler.Result.STOP_HANDLING
          // @ts-expect-error Due to lack of proper typings for cheetah actions
          case CheetahMessageAction.SetContext:
            this.onCheetahSetContext(msg)
            return CAPI.ExternalHandler.Result.STOP_HANDLING
          default:
            return CAPI.ExternalHandler.Result.CONTINUE_HANDLING
        }
      })

      this.capiWsConnection = capi

      await this.ensureCapiConnection()
      getLogger('cms.capi.ws').info(
        '[WSConnection] WebSocket connection and Cheetah session established successfully:',
        {
          gnarContainerId: this.gnarContainerIdCache,
        }
      )
    } catch (e) {
      getLogger('cms.capi.ws').error('[WSConnection] initiateWSConnection failed:', {
        errorMessage: e instanceof Error ? e.message : String(e),
        errorName: e instanceof Error ? e.name : typeof e,
        stackTrace: e instanceof Error ? e.stack : undefined,
        isAnonymous: this.options.isAnonymous,
        gnarContainerId: this.gnarContainerIdCache,
        errorObject: e,
      })
      this.options.cheetahCallbacks.onError(e)
    }
  }

  async sendPrompt(prompt: string): Promise<boolean> {
    try {
      await this.ensureCapiConnection()

      this.addMessageToQueue(prompt)

      const delay = 10000
      if (this.isCheetahSessionStarted) {
        return await this.sendMessagesFromQueue()
      } else {
        // If the setContext does not come back after 10 seconds, we'll attempt to send the prompt with fake state.
        // Maybe we should consider not doing this.
        this.messageQueueTimeout = setTimeout(() => {
          this.sendMessagesFromQueue()
        }, delay)
      }

      return true
    } catch (e) {
      getLogger('cms.capi.ws').error('[SendPrompt] client.sendPrompt failed with exception:', {
        errorMessage: e instanceof Error ? e.message : String(e),
        errorName: e instanceof Error ? e.name : typeof e,
        stackTrace: e instanceof Error ? e.stack : undefined,
        errorObject: e,
        gnarContainerId: this.gnarContainerIdCache,
      })
      this.options.cheetahCallbacks.onError(e)
      return false
    }
  }

  // =====================================
  // PRIVATE METHODS
  // =====================================

  private async getGnarContainerId(): Promise<string> {
    if (this.gnarContainerIdCache === undefined) {
      this.gnarContainerIdCache = await this.options.gnarContainerId
    }
    return this.gnarContainerIdCache
  }

  private assertCheetahEnabled = async () => {
    if (!this.options.isAnonymous) {
      try {
        const resp = await this.options.cheetah.getStatus()
        if (!resp.cheetahEnabled) {
          throw new WSError('Cheetah is disabled', resp.status)
        }
      } catch (e) {
        throw new WSError('Cheetah getStatus failed', e)
      }
    }
  }

  private assertCapiWsConnection<T>(
    capiWsConnection: T | undefined,
    rootErrMsg: string
  ): asserts capiWsConnection is T {
    if (!capiWsConnection) {
      throw new WSError(rootErrMsg, 'CAPI websocket is not initialized')
    }
  }

  private extractUsageDataFromMessage = (
    e: CheetahResultMessage | CheetahSetContextMessage
  ): BackendPromptCap | undefined => {
    if (e.usage && typeof e.usage === 'object') {
      const { promptsRemaining, refillDate } = e.usage
      if (typeof promptsRemaining === 'number' && typeof refillDate === 'string') {
        return { promptsRemaining, refillDate }
      } else {
        logError('TypeError: Cheetah usage object did not match expected shape', LogErrorMetrics.GOToolError)
      }
    }
  }

  private async connectCapi() {
    this.assertCapiWsConnection(this.capiWsConnection, 'Failed to start CAPI session')
    await this.capiWsConnection.connect(() => Change.resFromText(''))
  }

  private async startCapiSession() {
    this.assertCapiWsConnection(this.capiWsConnection, 'Failed to start CAPI session')
    const gnarContainerId = await this.getGnarContainerId()

    await this.capiWsConnection.client.setSessionOption({
      kind: CAPI.CAPISessionOptionType.GnarContainerId,
      id: gnarContainerId,
    })()
  }

  private async ensureCapiConnection() {
    // If the user is disconnected, we want to initiate the WS connection when the user clicks the CTA.
    if (this.capiWsConnection == null) {
      getLogger('cms.capi.ws').info('[WSConnection]WebSocket connection not available, initiating new connection', {
        gnarContainerId: this.gnarContainerIdCache,
      })
      await this.initiateWSConnection()
    } else if (this.capiWsConnection.isDisconnected || this.capiWsConnection.isIdle) {
      getLogger('cms.capi.ws').info('[WSConnection] WebSocket connection is disconnected or idle, reconnecting', {
        gnarContainerId: this.gnarContainerIdCache,
      })
      await this.connectCapi()
      await this.startCapiSession()
      await this.sendCheetahGetContextCommand()
    }
  }

  private sendCheetahGetContextCommand = async () => {
    this.assertCapiWsConnection(this.capiWsConnection, 'Failed to get initial Cheetah context')

    return await this.capiWsConnection.client.enqueueExternalCommand(({ id, currentRevision }) => ({
      id,
      rev: currentRevision,
      action: 'cheetah:getContext',
      generationMode: 'ideation',
    }))()
  }

  private sendCheetahActionCommand = async (prompt: string) => {
    this.assertCapiWsConnection(this.capiWsConnection, 'Failed to send Cheetah action')

    await this.capiWsConnection.client.enqueueExternalCommand(({ id, currentRevision }) => ({
      id,
      rev: currentRevision,
      action: 'cheetah:action',
      actionId,
      args: [{ name: 'answer', value: prompt }],
      contextId: this.meta.contextId,
      triggerContextId: this.meta.contextId,
      stateBlob: this.stateBlob,
    }))()

    this.options.cheetahCallbacks.onCheetahActionSend?.()
  }

  private addMessageToQueue = (prompt: string) => {
    if (typeof this.messageQueue !== 'string') {
      this.messageQueue = prompt
    }
  }

  private sendMessagesFromQueue = async (): Promise<boolean> => {
    if (this.messageQueueTimeout) {
      clearTimeout(this.messageQueueTimeout)
    }
    const prompt = this.messageQueue
    this.messageQueue = undefined

    if (typeof prompt === 'string') {
      const errMsg = 'Failed to send cheetah action in queue'
      try {
        if (!this.meta.contextId) {
          throw new WSError(errMsg, 'contextId was not set')
        }

        if (!this.stateBlob) {
          throw new WSError(errMsg, 'state blob is empty')
        }

        await this.sendCheetahActionCommand(prompt)

        return true
      } catch (e) {
        getLogger('cms.capi.ws').error('[MessageQueue] sendMessagesFromQueue failed with exception:', {
          errorMessage: e instanceof Error ? e.message : String(e),
          errorName: e instanceof Error ? e.name : typeof e,
          stackTrace: e instanceof Error ? e.stack : undefined,
          errorObject: e,
          gnarContainerId: this.gnarContainerIdCache,
        })
        this.options.cheetahCallbacks.onError(e)
        return false
      }
    }
    return true
  }

  // =====================================
  // CALLBACKS
  // =====================================

  private onCapiSessionStarted = (event: CAPI.SessionStarted) => {
    if (!event.allowedFeatures.includes('CHEETAH')) {
      const wsError = new WSError('CAPI WS initialization error', 'no cheetah access')
      if (this.options.isAnonymous) {
        wsError._promptSignup = 'popup'
      }
      getLogger('cms.capi.ws').error('[CapiSessionStarted] CAPI WS initialization error - no cheetah access:', {
        errorMessage: wsError._msg,
        errorReason: wsError._reason,
        errorKind: wsError._kind,
        promptSignup: wsError._promptSignup,
        gnarContainerId: this.gnarContainerIdCache,
      })
      this.options.cheetahCallbacks.onError(wsError)
    } else if (event.cheetahState?.usage) {
      this.options.callbacks.onSyncBackendPromptCap(event.cheetahState.usage)
    }
  }

  private onCapiErrorReceived = (e: CAPI.ErrorReceived) => {
    const wsError = new WSError(e.error, 'CAPI WS Error')
    getLogger('cms.capi.ws').error('[CapiErrorReceived] CAPI WS error:', {
      errorName: e.error,
      errorKind: e.kind,
      errorCode: e.code,
      errorDetails: e.details,
      errorMessage: e.message,
      errorReason: e.reason,
      gnarContainerId: this.gnarContainerIdCache,
    })
    this.options.cheetahCallbacks.onError(wsError)
  }

  private onCapiDisconnect = (_e: CAPI.OnDisconnect) => {
    this.isCheetahSessionStarted = false
    // Reset meta state to prevent message ordering issues on reconnect
    this.meta = { highestChunkId: 0, contextId: contextIdMock }
    // We will reconnect on next action
  }

  private onCheetahResult = (e: CheetahResultMessage) => {
    const doneId = e.id

    if (doneId <= this.meta.highestChunkId) {
      // doneId for a previous request. Ignore
    } else {
      const backendPromptCap = this.extractUsageDataFromMessage(e)
      if (backendPromptCap) {
        this.options.callbacks.onSyncBackendPromptCap(backendPromptCap)
      }

      const cheetahError = e.error

      if (cheetahError) {
        getLogger('cms.capi.ws').error('[CheetahResult] Cheetah result error received:', {
          errorKind: cheetahError.kind,
          errorTitle: cheetahError.title,
          errorText: cheetahError.text,
          messageId: e.id,
          fullMessage: e,
          hasPromptsLimitExceeded: cheetahError.kind === 'promptsLimitExceeded',
          gnarContainerId: this.gnarContainerIdCache,
        })

        this.options.cheetahCallbacks.onError(
          new WSError(
            'Error in onCheetahResult',
            e.error,
            cheetahError.kind === 'promptsLimitExceeded' ? 'popup' : undefined,
            cheetahError.kind
          )
        )
      } else if (doneId === this.meta.highestChunkId + 1) {
        this.meta.doneId = doneId
        this.options.cheetahCallbacks.onCheetahResult(e)
      } else {
        // For network reasons, it's possible to receive the done message before the result has begun streaming
        // This can happen especially after reconnection, so we should still call the callback
        this.meta.doneId = doneId
        this.options.cheetahCallbacks.onCheetahResult(e)
      }
    }
  }

  private onCheetahChunk = (e: CheetahChunkMessage) => {
    const chunkId = e.id
    if (chunkId > this.meta.highestChunkId) {
      this.meta.highestChunkId = chunkId
    }

    this.options.cheetahCallbacks.onCheetahChunk(e)
  }

  private onCheetahError = (e: CheetahErrorMessage) => {
    getLogger('cms.capi.ws').error('[CheetahError] Cheetah error message received:', {
      errorKind: e.error.kind,
      errorTitle: e.error.title,
      errorText: e.error.text,
      messageId: e.id,
      fullErrorMessage: e,
      gnarContainerId: this.gnarContainerIdCache,
    })
    this.options.cheetahCallbacks.onError(new WSError('Cheetah error', e, undefined, e.error.kind))
  }

  private onCheetahSetContext = (e: CheetahSetContextMessage) => {
    this.isCheetahSessionStarted = true

    this.meta.contextId = e.context.id
    if (e.id > this.meta.highestChunkId) {
      this.meta.highestChunkId = e.id
    }
    this.stateBlob = e.context.stateBlob
    const backendPromptCap = this.extractUsageDataFromMessage(e)
    if (backendPromptCap) {
      this.options.callbacks.onSyncBackendPromptCap(backendPromptCap)

      // If we are out of prompts and have messages in the queue, we should trigger the wall instead of sending the messages. Should only happen on initial click for anonymous users.
      if (backendPromptCap.promptsRemaining === 0 && this.messageQueue) {
        this.options.callbacks.onTriggerWall()
        this.options.cheetahCallbacks.onCheetahSetContext(e)
        this.messageQueue = undefined
        return
      }
    }
    this.options.cheetahCallbacks.onCheetahSetContext(e)
    void this.sendMessagesFromQueue()
  }
}
